import { Link } from 'react-router-dom';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Metric, Spinner, StateBlock } from '../../../components/ui/Feedback';
import { ActionMenu } from '../../../components/ui/ActionMenu';
import { EditIcon, TrashIcon } from '../../../components/icons/Icons';
import { formatDate, formatDaysLeft } from '../../../lib/format';
import { rfpStatusMeta } from '../../../lib/status';
import { errorMessage } from '../../../lib/api';
import type { RfpListItem } from '../../../types/api';
import { CategoryCell } from './CategoryCell';
import { ProductCell } from './ProductCell';

/**
 * Column widths reverse-engineered from the design's rendered header baselines
 * (RFP No. 19px, Product Category 112px, Product 257px ... across a 1304px
 * table with a uniform 16px cell inset). The labels fit their columns exactly.
 */
export const RFP_COLUMNS = [
  { key: 'rfpNumber', label: 'RFP No.', width: '7.29%', center: false },
  { key: 'category', label: 'Product Category', width: '11.12%', center: false },
  { key: 'product', label: 'Product', width: '15.18%', center: false },
  { key: 'createdOn', label: 'Created On', width: '8.67%', center: false },
  { key: 'vendorCount', label: 'Vendor', width: '7.98%', center: false },
  { key: 'contactedCount', label: 'Contacted', width: '8.74%', center: false },
  { key: 'responseCount', label: 'Response', width: '8.13%', center: false },
  { key: 'completedCount', label: 'Completed', width: '8.05%', center: false },
  { key: 'daysLeft', label: 'Days Left', width: '8.59%', center: false },
  { key: 'status', label: 'Status', width: '8.74%', center: true },
  { key: 'action', label: 'Action', width: '7.52%', center: true },
] as const;

interface RfpTableProps {
  items: RfpListItem[];
  loading: boolean;
  error: unknown;
  emptyMessage: string;
  onRetry: () => void;
  onEdit: (rfp: RfpListItem) => void;
  onDelete: (rfp: RfpListItem) => void;
}

export function RfpTable({
  items,
  loading,
  error,
  emptyMessage,
  onRetry,
  onEdit,
  onDelete,
}: RfpTableProps) {
  const columnCount = RFP_COLUMNS.length;

  return (
    <div className="table-wrap">
      <table className="data-table">
        <colgroup>
          {RFP_COLUMNS.map((column) => (
            <col key={column.key} style={{ width: column.width }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {RFP_COLUMNS.map((column) => (
              <th key={column.key} className={column.center ? 'cell-center' : undefined}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {loading && items.length === 0 ? (
            <tr>
              <td colSpan={columnCount}>
                <div className="state-block">
                  <Spinner />
                </div>
              </td>
            </tr>
          ) : null}

          {!loading && error ? (
            <tr>
              <td colSpan={columnCount}>
                <StateBlock
                  title="Could not load RFPs"
                  description={errorMessage(error)}
                  action={
                    <Button variant="outlined" onClick={onRetry}>
                      Try again
                    </Button>
                  }
                />
              </td>
            </tr>
          ) : null}

          {!loading && !error && items.length === 0 ? (
            <tr>
              <td colSpan={columnCount}>
                <StateBlock title={emptyMessage} />
              </td>
            </tr>
          ) : null}

          {items.map((rfp) => {
            const status = rfpStatusMeta(rfp.status);
            return (
              <tr key={rfp.id}>
                <td>
                  <Link to={`/rfp/${rfp.id}`} className="link-plain">
                    {rfp.rfpNumber}
                  </Link>
                </td>
                <td>
                  <CategoryCell names={rfp.categories.names} count={rfp.categories.count} />
                </td>
                <td>
                  <ProductCell names={rfp.products.names} count={rfp.products.count} />
                </td>
                <td>{formatDate(rfp.createdOn)}</td>
                <td>
                  <Metric
                    value={rfp.vendorCount}
                    hint={`${rfp.vendorCount} vendors invited to ${rfp.rfpNumber}`}
                  />
                </td>
                <td>
                  <Metric value={rfp.contactedCount} hint="Vendors contacted" />
                </td>
                <td>
                  <Metric value={rfp.responseCount} hint="Responses received" />
                </td>
                <td>
                  <Metric value={rfp.completedCount} hint="Vendors completed" />
                </td>
                <td className="is-numeric">{formatDaysLeft(rfp.daysLeft)}</td>
                <td className="cell-center">
                  <Badge variant={status.variant}>{status.label}</Badge>
                </td>
                <td className="cell-center">
                  <ActionMenu
                    label={`Actions for ${rfp.rfpNumber}`}
                    items={[
                      {
                        key: 'edit',
                        label: 'Edit',
                        icon: <EditIcon size={16} />,
                        onSelect: () => onEdit(rfp),
                      },
                      {
                        key: 'delete',
                        label: 'Delete',
                        icon: <TrashIcon size={16} />,
                        danger: true,
                        separatorBefore: true,
                        onSelect: () => onDelete(rfp),
                      },
                    ]}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
